import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles/tw.merge";

import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { StoreSearchInput } from "@/components/Store/StoreSearchInput/storeSearchInput";
import {
    SearchItem,
    SearchItemType
} from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useWoosmapServices } from "@/utils/hooks/useWoosmapServices.hook";

import {
    STORE_PICKER_INPUT_TYPES,
    STORE_PICKER_MESSAGE_ID
} from "../../storePicker.config";
import { useStorePickerContext } from "../../storePicker.context";
import {
    fetchLocalityDetails,
    fetchLocalityGeocode
} from "./storePickerInput.helpers";

type Props = {
    className?: string;
};

const StorePickerInput = ({ className }: Props) => {
    const { t } = useGranite();

    const { searchItem, setSearchItem, setSelectedStore, config } =
        useStorePickerContext();

    const { LocalitiesService } = useWoosmapServices();

    const clearSelectedStore = () => setSelectedStore(null);

    const handleOnUseLocation = (latitude: number, longitude: number) => {
        if (!isDefined(LocalitiesService)) return;

        clearMessageById(STORE_PICKER_MESSAGE_ID);

        const coordinates = {
            lat: latitude,
            lng: longitude
        };

        fetchLocalityGeocode(LocalitiesService, coordinates)
            .then((result) => ({
                value: result.public_id,
                latitude: result.geometry?.location.lat,
                longitude: result.geometry?.location.lng,
                label: t("form.storePicker.myLocation"),
                type: SearchItemType.Locality
            }))
            .then(setSearchItem)
            .catch(handleOnLocalityNotFoundError)
            .finally(clearSelectedStore);
    };

    const handleOnSelect = (searchItem: SearchItem | null) => {
        if (!isDefined(LocalitiesService)) return;

        clearMessageById(STORE_PICKER_MESSAGE_ID);

        if (!isDefined(searchItem)) return;

        if (searchItem.type === SearchItemType.Locality) {
            fetchLocalityDetails(LocalitiesService, searchItem.value)
                .then((result) => ({
                    ...searchItem,
                    latitude: result.geometry?.location.lat,
                    longitude: result.geometry?.location.lng
                }))
                .then(setSearchItem)
                .catch(handleOnLocalityNotFoundError)
                .finally(clearSelectedStore);
        } else {
            setSearchItem(searchItem);
            clearSelectedStore();
        }
    };

    const handleOnLocalityNotFoundError = () =>
        broadcastMessage({
            id: STORE_PICKER_MESSAGE_ID,
            text: t("storesearch.locality.notFound"),
            status: "danger",
            dismissible: true
        });

    return (
        <StoreSearchInput
            clearable={false}
            menuPortalTarget={null}
            messageId={STORE_PICKER_MESSAGE_ID}
            value={searchItem}
            itemsToRetrieve={STORE_PICKER_INPUT_TYPES}
            placeholder={t("form.storePicker.input.placeholder")}
            loadingText={t("form.storePicker.loading")}
            aria-label={t("accessibility.storePicker.input.label")}
            onSelect={handleOnSelect}
            onUseLocation={handleOnUseLocation}
            filterStoreType={config.filterStoreType}
        >
            <InputGroup className={className}>
                <StoreSearchInput.Input className={cn("tw:w-full")} />
                <StoreSearchInput.SearchButton className={cn("tw:min-w-fit")}>
                    {t("form.storePicker.submit")}
                </StoreSearchInput.SearchButton>
            </InputGroup>
            <StoreSearchInput.Location />
        </StoreSearchInput>
    );
};

export { StorePickerInput };
